import type {Theme} from '@emotion/react';

/** The available size variants for the {@link Headline} component. */
export type HeadlineSize = 'display' | 'headline' | 'title';

/** The available size variants for the {@link P} component. */
export type PSize = 'body' | 'bodySmall' | 'labelMedium' | 'labelSmall';

/**
 * Returns the font-family for the given headline size.
 * Display and headline sizes use NotoSerif, title uses PlusJakartaSans.
 *
 * @param props       The styled component props.
 * @param props.$size The headline size variant.
 * @param props.theme The Emotion theme.
 * @returns The CSS font-family value.
 */
export const getHeadlineFontFamily = ({$size, theme}: {$size?: HeadlineSize; theme: Theme}) => {
    switch ($size) {
        case 'display':
        case 'headline':
            return theme.fonts.NotoSerif;
        case 'title':
        default:
            return theme.fonts.PlusJakartaSans;
    }
};

/**
 * Returns the font-size for the given headline size.
 *
 * @param props       The styled component props.
 * @param props.$size The headline size variant.
 * @returns The CSS font-size value in rem.
 */
export const getHeadlineFontSize = ({$size}: {$size?: HeadlineSize}) => {
    switch ($size) {
        case 'display':
            return '4.4rem';
        case 'title':
            return '1.6rem';
        case 'headline':
        default:
            return '2.4rem';
    }
};

/**
 * Returns the font-weight for the given headline size.
 *
 * @param props       The styled component props.
 * @param props.$size The headline size variant.
 * @returns The CSS font-weight value.
 */
export const getHeadlineFontWeight = ({$size}: {$size?: HeadlineSize}) => {
    switch ($size) {
        case 'title':
            return '500';
        case 'display':
        case 'headline':
        default:
            return '700';
    }
};

/**
 * Returns the line-height for the given headline size.
 *
 * @param props       The styled component props.
 * @param props.$size The headline size variant.
 * @returns The CSS line-height value.
 */
export const getHeadlineLineHeight = ({$size}: {$size?: HeadlineSize}) => {
    switch ($size) {
        case 'display':
            return '1.1';
        case 'title':
            return '1.3';
        case 'headline':
        default:
            return '1.2';
    }
};

/**
 * Returns the font-size for the given paragraph size.
 *
 * @param props       The styled component props.
 * @param props.$size The paragraph size variant.
 * @returns The CSS font-size value in rem.
 */
export const getPFontSize = ({$size}: {$size?: PSize}) => {
    switch ($size) {
        case 'bodySmall':
            return '1.3rem';
        case 'labelMedium':
            return '1.2rem';
        case 'labelSmall':
            return '1.1rem';
        case 'body':
        default:
            return '1.5rem';
    }
};

/**
 * Returns the font-weight for the given paragraph size.
 *
 * @param props       The styled component props.
 * @param props.$size The paragraph size variant.
 * @returns The CSS font-weight value.
 */
export const getPFontWeight = ({$size}: {$size?: PSize}) => {
    switch ($size) {
        case 'bodySmall':
        case 'labelMedium':
            return '600';
        case 'labelSmall':
            return '500';
        case 'body':
        default:
            return '400';
    }
};

/**
 * Returns the line-height for the given paragraph size.
 *
 * @param props       The styled component props.
 * @param props.$size The paragraph size variant.
 * @returns The CSS line-height value.
 */
export const getPLineHeight = ({$size}: {$size?: PSize}) => {
    switch ($size) {
        case 'bodySmall':
        case 'labelMedium':
        case 'labelSmall':
            return '1.4';
        case 'body':
        default:
            return '1.5';
    }
};

/**
 * Returns the letter-spacing for the given paragraph size.
 * Only labelMedium uses letter-spacing (uppercase metadata style).
 *
 * @param props       The styled component props.
 * @param props.$size The paragraph size variant.
 * @returns The CSS letter-spacing value.
 */
export const getPLetterSpacing = ({$size}: {$size?: PSize}) => {
    switch ($size) {
        case 'labelMedium':
            return '0.15rem';
        default:
            return 'normal';
    }
};

/**
 * Returns the text-transform for the given paragraph size.
 * Only labelMedium is uppercase.
 *
 * @param props       The styled component props.
 * @param props.$size The paragraph size variant.
 * @returns The CSS text-transform value.
 */
export const getPTextTransform = ({$size}: {$size?: PSize}) => {
    switch ($size) {
        case 'labelMedium':
            return 'uppercase';
        default:
            return 'none';
    }
};